import React, { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'react-toastify';
import api from '../../utils/api';
import './PaymentManagement.css';

const PaymentManagement = () => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [selectedScreenshot, setSelectedScreenshot] = useState(null);
  // Get current month in YYYY-MM format
  const getCurrentMonthYear = () => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  };

  const [monthYearFilter, setMonthYearFilter] = useState('');
  const [totalRevenue, setTotalRevenue] = useState(0);
  const [filters, setFilters] = useState({
    status: ''
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearching, setIsSearching] = useState(false);

  const fetchPayments = useCallback(async () => {
    try {
      setLoading(true);
      setIsSearching(true);
      setError('');
      
      const params = new URLSearchParams();
      
      // Only apply month filter if explicitly selected
      if (monthYearFilter) {
        params.set('monthYear', monthYearFilter);
      }
      
      // Add search term if provided
      if (searchTerm.trim()) {
        params.set('search', searchTerm.trim());
      }
      
      // Add status filter if selected
      if (filters.status) {
        params.set('status', filters.status);
      }
      
      const url = `/api/payments/all?${params.toString()}`;
      const res = await api.get(url);
      
      setPayments(res.data);
      
      // Calculate total revenue for the current view
      const filteredPayments = res.data.filter(payment => {
        // If month filter is selected, only include payments from that month
        if (monthYearFilter) {
          const paymentDate = new Date(payment.createdAt);
          const [year, month] = monthYearFilter.split('-');
          return (
            paymentDate.getFullYear() === parseInt(year) &&
            paymentDate.getMonth() + 1 === parseInt(month)
          );
        }
        return true;
      });
      
      // Calculate revenue based on status filter
      const relevantPayments = filters.status 
        ? filteredPayments.filter(payment => payment.status === filters.status)
        : filteredPayments.filter(payment => payment.status === 'verified');
        
      const revenue = relevantPayments.reduce(
        (sum, payment) => sum + (parseFloat(payment.amount) || 0), 
        0
      );
      
      setTotalRevenue(revenue);
    } catch (err) {
      console.error('Error fetching payments:', err);
      setError('Failed to load payments. Please try again.');
      toast.error('Failed to fetch payments');
    } finally {
      setLoading(false);
      setIsSearching(false);
    }
  }, [monthYearFilter, filters.status, searchTerm]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchPayments();
  };
  
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Call fetchPayments when component mounts and when filters change
  useEffect(() => {
    fetchPayments();
  }, [fetchPayments]);

  const handleDeletePayment = async (paymentId) => {
    if (confirmDelete === paymentId) {
      try {
        await api.delete(`/api/payments/${paymentId}`);
        toast.success('Payment deleted successfully');
        // Refresh the payments list
        fetchPayments();
      } catch (err) {
        console.error('Error deleting payment:', err);
        toast.error(err.response?.data?.message || 'Failed to delete payment');
      } finally {
        setConfirmDelete(null);
      }
    } else {
      // First click - ask for confirmation
      setConfirmDelete(paymentId);
      // Auto-reset after 5 seconds
      setTimeout(() => {
        setConfirmDelete(null);
      }, 5000);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'pending':
        return 'status-badge pending';
      case 'submitted':
        return 'status-badge submitted';
      case 'verified':
        return 'status-badge verified';
      case 'rejected':
        return 'status-badge rejected';
      case 'refunded':
        return 'status-badge refunded';
      default:
        return 'status-badge';
    }
  };

  if (loading) {
    return <div className="admin-loading">Loading payments...</div>;
  }

  if (error) {
    return <div className="admin-error">{error}</div>;
  }

  return (
    <div className="payment-management">
      <h2>Payment Management</h2>
      
      <div className="filters-container">
        <div className="filter-group">
          <label>Month:</label>
          <input
            type="month"
            value={monthYearFilter}
            onChange={(e) => setMonthYearFilter(e.target.value)}
            className="filter-select"
          />
        </div>
        
        <div className="filter-group">
          <label>Payment Status:</label>
          <select
            name="status"
            value={filters.status}
            onChange={handleFilterChange}
            className="filter-select"
          >
            <option value="">All Statuses</option>
            <option value="pending">Pending</option>
            <option value="submitted">Submitted</option>
            <option value="verified">Verified</option>
            <option value="rejected">Rejected</option>
            <option value="refunded">Refunded</option>
          </select>
        </div>
        
        <form onSubmit={handleSearch} className="search-form">
          <input
            type="text"
            placeholder="Search by user or verifier name"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          <button 
            type="submit" 
            className="search-button"
            disabled={isSearching}
          >
            {isSearching ? 'Searching...' : 'Search'}
          </button>
        </form>
        
        <div className="revenue-summary">
          {monthYearFilter || getCurrentMonthYear()} Revenue: ₹{totalRevenue.toLocaleString()}
          {filters.status && ` (${filters.status.charAt(0).toUpperCase() + filters.status.slice(1)})`}
        </div>
      </div>
      
      <div className="admin-actions">
        <button 
          className="refresh-button"
          onClick={fetchPayments}
        >
          Refresh Payments
        </button>
      </div>
      
      {payments.length === 0 ? (
        <p className="no-data">No payments found</p>
      ) : (
        <div className="table-responsive" style={{ overflowX: 'auto', maxWidth: '100%' }}>
          <table className="admin-table" style={{ minWidth: '1000px' }}>
            <thead>
              <tr>
                <th>S.No</th>
                <th>User</th>
                <th>Amount</th>
                <th>Type</th>
                <th>Status</th>
                <th>Transaction ID</th>
                <th>Created</th>
                <th>Verified By</th>
                <th>Payment Proof</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {payments.map((payment, index) => (
                <tr key={payment._id}>
                  <td>{index + 1}</td>
                  <td>
                    {payment.paidBy ? (
                      <span title={payment.paidBy.email}>{payment.paidBy.name}</span>
                    ) : 'Unknown'}
                  </td>
                  <td>
                    <div><strong>Total:</strong> {payment.currency} {payment.amount}</div>
                    <div><strong>S30 Balance:</strong> {payment.currency} {payment.usedS30Balance || 0}</div>
                    <div><strong>UPI/Zelle:</strong> {payment.currency} {payment.remainingAmount || payment.amount}</div>
                  </td>
                  <td>
                    {payment.isPreBooking ? 'Pre-booking' : 'Regular'}
                  </td>
                  <td>
                    <span className={getStatusBadgeClass(payment.status)}>
                      {payment.status.charAt(0).toUpperCase() + payment.status.slice(1)}
                    </span>
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {payment.transactionId || 'N/A'}
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {formatDate(payment.createdAt)}
                  </td>
                  <td>
                    {payment.verifiedBy ? payment.verifiedBy.name : 'N/A'}
                  </td>
                  <td>
                    {payment.transactionScreenshotUrl ? (
                      <button 
                        className="view-proof-button"
                        onClick={() => setSelectedScreenshot(payment.transactionScreenshotUrl)}
                      >
                        View Proof
                      </button>
                    ) : (
                      <span className="no-proof">No proof</span>
                    )}
                  </td>
                  <td>
                    <button
                      className={`delete-button ${confirmDelete === payment._id ? 'confirm' : ''}`}
                      onClick={() => handleDeletePayment(payment._id)}
                    >
                      {confirmDelete === payment._id ? 'Confirm' : 'Delete'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal for displaying payment proof */}
      {selectedScreenshot && (
        <div className="screenshot-modal-overlay" onClick={() => setSelectedScreenshot(null)}>
          <div className="screenshot-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="screenshot-modal-header">
              <h3>Payment Proof</h3>
              <button 
                className="close-modal-button"
                onClick={() => setSelectedScreenshot(null)}
              >
                ×
              </button>
            </div>
            <div className="screenshot-modal-body">
              <img 
                src={selectedScreenshot} 
                alt="Payment Proof" 
                className="screenshot-image"
              />
            </div>
            <div className="screenshot-modal-footer">
              <button 
                className="open-in-new-tab-button"
                onClick={() => window.open(selectedScreenshot, '_blank')}
              >
                Open in New Tab
              </button>
              <button 
                className="close-button"
                onClick={() => setSelectedScreenshot(null)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentManagement;
